import type {
  CreateOneWorkerRunRequestBodyType,
  GetListWorkerRunFiltersType,
  WorkerRunReportType,
  WorkerRunType,
} from "../schemas/WorkerSchemas.ts";
import type {
  CreateOneRequestType,
  CreateOneResponseType,
  GetListRequestType,
  GetListResponseType,
  GetOneRequestType,
  GetOneResponseType,
  ResourceAdapterToAdapterQueryKeysType,
  ResourceAdapterType,
} from "../util/index.ts";
import { fetchAndCollate, getFilterObject, Resources } from "../util/index.ts";
import { restAPI } from "./api.ts";

/**
 * Worker run API (`/worker_runs/*`): list/search, fetch by id, and enqueue a run.
 */
export const WorkerRunAdapter: WorkerRunAdapterType = {
  /**
   * Search worker runs (paginated).
   */
  getList: ({ filters, page, pageSize, sortBy, sortDirection }) =>
    fetchAndCollate(
      ({ page: pageNum, pageSize: pageSizeNum }) =>
        restAPI.nodeAPI.POST<GetListResponseType<WorkerRunType>>({
          endpoint: "/worker_runs/search",
          body: {
            page: pageNum,
            page_size: pageSizeNum,
            sort_by: sortBy,
            sort_direction: sortDirection,
            filter: getFilterObject(filters),
          },
        }),
      page,
      pageSize
    ),

  /**
   * Get a single worker run by id.
   */
  getOne: async ({ id }) => {
    const resp = await restAPI.nodeAPI.GET<WorkerRunType>({
      endpoint: `/worker_runs/${id}`,
    });
    return { data: resp };
  },

  /**
   * Create / enqueue a worker run (worker id, version, inputs).
   */
  createOne: async ({ data }) => {
    const resp = await restAPI.nodeAPI.POST<WorkerRunType>({
      endpoint: "/worker_runs",
      body: data,
    });
    return { data: resp };
  },
  reports: {
    getList: ({ id }) =>
      restAPI.nodeAPI.GET<WorkerRunReportType[]>({
        endpoint: `/worker_runs/${id}/reports`,
      }),
  },
};

export interface WorkerRunAdapterType
  extends Pick<
    ResourceAdapterType<WorkerRunType>,
    "getList" | "getOne" | "createOne"
  > {
  getList: (
    params: GetListRequestType<GetListWorkerRunFiltersType>
  ) => Promise<GetListResponseType<WorkerRunType>>;
  getOne: (
    params: GetOneRequestType<{ id: string }>
  ) => Promise<GetOneResponseType<WorkerRunType>>;
  createOne: (
    params: CreateOneRequestType<CreateOneWorkerRunRequestBodyType>
  ) => Promise<CreateOneResponseType<WorkerRunType>>;
  reports: {
    getList: (
      params: GetOneRequestType<{ id: string }>
    ) => Promise<WorkerRunReportType[]>;
  };
}

export const WorkerRunAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<WorkerRunAdapterType> =
  {
    getList: (params) => [Resources.WORKER_RUN, "getList", params] as const,
    getOne: (params) => [Resources.WORKER_RUN, "getOne", params.id] as const,
    createOne: (params) => [Resources.WORKER_RUN, "createOne", params] as const,
    reports: {
      getList: (params) =>
        [Resources.WORKER_RUN, "reports", "getList", params.id] as const,
    },
  };
